import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  MapPin, User, UserPlus, Trash2, Building2, Package, ShoppingCart, Factory, 
  AlertCircle, CheckCircle2, Loader2, Pencil, X, FileText, Settings, Plus,
  Warehouse, HardHat, Tag, ClipboardList, Layers, Calculator, Users, Briefcase
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping from string names to Lucide components
const ICON_MAP = {
  'warehouse': Warehouse,
  'shopping-cart': ShoppingCart,
  'hard-hat': HardHat,
  'tag': Tag,
  'clipboard-list': ClipboardList,
  'layers': Layers,
  'calculator': Calculator,
  'factory': Factory,
  'package': Package,
  'file-text': FileText,
  'building': Building2,
  'users': Users,
  'briefcase': Briefcase,
  'user': User,
};

// Color mapping from string names to Tailwind classes
const COLOR_MAP = {
  'blue': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-600' },
  'green': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: 'text-green-600' },
  'orange': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', icon: 'text-orange-600' },
  'purple': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', icon: 'text-purple-600' },
  'cyan': { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200', icon: 'text-cyan-600' },
  'pink': { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200', icon: 'text-pink-600' },
  'red': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: 'text-red-600' },
  'indigo': { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', icon: 'text-indigo-600' },
  'emerald': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'text-emerald-600' },
  'amber': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-600' },
  'gray': { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', icon: 'text-gray-600' },
};

const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || User;
};

const getColorClasses = (colorName) => {
  return COLOR_MAP[colorName] || COLOR_MAP['gray'];
};

const LocationTeamMappingPage = () => {
  const [groupedMappings, setGroupedMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  // Assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  // Manage Roles dialog state
  const [showManageRolesDialog, setShowManageRolesDialog] = useState(false);
  const [showRoleFormDialog, setShowRoleFormDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    role_key: '',
    label: '',
    description: '',
    is_location_based: true,
    is_centralized: false,
    is_multi_assignable: false,
    has_sub_division: false,
    sub_division_type: null,
    icon: 'user',
    color: 'gray'
  });
  const [savingRole, setSavingRole] = useState(false);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [mappingsRes, usersRes, processesRes, rolesRes] = await Promise.all([
        fetch(`${API}/api/location-team-mappings/grouped`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/processes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/location-team-mappings/roles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!mappingsRes.ok) throw new Error('Failed to fetch mappings');
      
      const mappings = await mappingsRes.json();
      const usersData = await usersRes.json();
      const processesData = await processesRes.json();
      const rolesData = await rolesRes.json();

      setGroupedMappings(mappings);
      setUsers(usersData.filter(u => u.is_active !== false));
      setProcesses(processesData);
      setRoles(rolesData.roles || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAssignDialog = (locationId, locationName, role, existingMapping = null) => {
    setAssignTarget({ 
      locationId, 
      locationName, 
      role, 
      existingMappingId: existingMapping?.id,
      existingUserId: existingMapping?.user_id 
    });
    setSelectedUserId(existingMapping?.user_id || '');
    // Handle both old (process_id) and new (process_ids) format
    const existingProcessIds = existingMapping?.process_ids || 
      (existingMapping?.process_id ? [existingMapping.process_id] : []);
    setSelectedProcessIds(existingProcessIds);
    setIsEditMode(!!existingMapping);
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    const token = localStorage.getItem('token');
    setSaving(assignTarget.role);

    // Check if role has sub-division (e.g., production_supervisor has processes)
    const roleConfig = getRoleByKey(assignTarget.role);
    const hasSubDivision = roleConfig?.has_sub_division;

    try {
      if (assignTarget.existingMappingId) {
        // Update existing mapping
        const res = await fetch(`${API}/api/location-team-mappings/${assignTarget.existingMappingId}`, {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ 
            user_id: selectedUserId,
            ...(hasSubDivision ? { process_ids: selectedProcessIds } : {})
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to update');
        }
        toast.success('Assignment updated');
      } else {
        // Create new mapping
        const res = await fetch(`${API}/api/location-team-mappings`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ 
            location_id: assignTarget.locationId,
            role: assignTarget.role,
            user_id: selectedUserId,
            ...(hasSubDivision ? { process_ids: selectedProcessIds } : {}),
            is_primary: true
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to create');
        }
        toast.success('Assignment created');
      }

      setShowAssignDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.message);
    } finally {
      setSaving(null);
    }
  };

  const handleRemove = async (mappingId, roleName) => {
    if (!window.confirm(`Are you sure you want to remove this ${roleName.replace('_', ' ')} assignment?`)) {
      return;
    }

    const token = localStorage.getItem('token');
    setSaving(mappingId);

    try {
      const res = await fetch(`${API}/api/location-team-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to remove');
      toast.success('Assignment removed');
      fetchData();
    } catch (error) {
      console.error('Error removing:', error);
      toast.error('Failed to remove assignment');
    } finally {
      setSaving(null);
    }
  };

  const toggleProcessSelection = (processId) => {
    setSelectedProcessIds(prev => {
      if (prev.includes(processId)) {
        return prev.filter(id => id !== processId);
      } else {
        return [...prev, processId];
      }
    });
  };

  // ==================== ROLE MANAGEMENT FUNCTIONS ====================
  
  const openRoleForm = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        role_key: role.value || role.role_key || '',
        label: role.label || '',
        description: role.description || '',
        is_location_based: role.is_location_based ?? true,
        is_centralized: role.is_centralized ?? false,
        is_multi_assignable: role.is_multi_assignable ?? false,
        has_sub_division: role.has_sub_division ?? false,
        sub_division_type: role.sub_division_type || null,
        icon: role.icon || 'user',
        color: role.color || 'gray'
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        role_key: '',
        label: '',
        description: '',
        is_location_based: true,
        is_centralized: false,
        is_multi_assignable: false,
        has_sub_division: false,
        sub_division_type: null,
        icon: 'user',
        color: 'gray'
      });
    }
    setShowRoleFormDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.role_key.trim() || !roleForm.label.trim()) {
      toast.error('Role key and label are required');
      return;
    }

    const token = localStorage.getItem('token');
    setSavingRole(true);

    try {
      const payload = {
        ...roleForm,
        role_key: roleForm.role_key.toLowerCase().replace(/\s+/g, '_')
      };

      if (editingRole) {
        // Update existing role
        const res = await fetch(`${API}/api/location-team-mappings/team-roles/${editingRole.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to update role');
        }
        toast.success('Role updated successfully');
      } else {
        // Create new role
        const res = await fetch(`${API}/api/location-team-mappings/team-roles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to create role');
        }
        toast.success('Role created successfully');
      }

      setShowRoleFormDialog(false);
      fetchData(); // Refresh to get updated roles
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system_role) {
      toast.error('Cannot delete system roles');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the "${role.label}" role? This action cannot be undone.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    setSavingRole(true);

    try {
      const res = await fetch(`${API}/api/location-team-mappings/team-roles/${role.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete role');
      }
      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.message);
    } finally {
      setSavingRole(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getRoleByKey = (roleKey) => {
    return roles.find(r => r.value === roleKey || r.role_key === roleKey);
  };

  const getRoleLabel = (roleValue) => {
    const role = getRoleByKey(roleValue);
    return role?.label || roleValue;
  };

  const getRoleIcon = (roleKey) => {
    const role = getRoleByKey(roleKey);
    const IconComponent = getIconComponent(role?.icon);
    const colors = getColorClasses(role?.color);
    return <IconComponent className={`w-4 h-4 ${colors.icon}`} />;
  };

  const getRoleColorClass = (roleKey) => {
    const role = getRoleByKey(roleKey);
    const colors = getColorClasses(role?.color);
    return `${colors.bg} ${colors.text} ${colors.border}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="space-y-6" data-testid="location-team-mapping-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-600" />
            Location Team Mapping
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign team members to roles for each unit. Used for automatic task routing.
          </p>
        </div>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => setShowManageRolesDialog(true)}
          data-testid="manage-roles-btn"
        >
          <Settings className="w-4 h-4" />
          Manage Roles
        </Button>
      </div>

      {/* Legend - Dynamic from roles */}
      <div className="flex flex-wrap gap-3">
        {roles.map(role => {
          const colors = getColorClasses(role.color);
          const IconComponent = getIconComponent(role.icon);
          return (
            <div key={role.value} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
              <IconComponent className="w-4 h-4" />
              <span className="text-xs font-medium">{role.label}</span>
            </div>
          );
        })}
      </div>

      {/* Location Cards */}
      <div className="grid gap-6">
        {groupedMappings.map(location => {
          // Separate centralized and location-based roles
          const centralizedRoles = roles.filter(r => r.is_centralized);
          const locationBasedRoles = roles.filter(r => r.is_location_based && !r.is_centralized);

          return (
            <Card key={location.location_id || 'all'} className="border border-gray-200" data-testid={`location-card-${location.location_code}`}>
              <CardHeader className="pb-3 bg-gray-50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-gray-600" />
                  {location.location_name}
                  <Badge variant="outline" className="ml-2 text-xs">{location.location_code}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Centralized Roles - Only for "All Locations" */}
                  {location.location_id === null && centralizedRoles.map(role => {
                    const mapping = location[role.value] || location.all_mappings?.find(m => m.role === role.value);
                    return (
                      <RoleCard
                        key={role.value}
                        role={role.value}
                        label={role.label}
                        icon={getRoleIcon(role.value)}
                        mapping={mapping}
                        colorClass={getRoleColorClass(role.value)}
                        onAssign={() => openAssignDialog(location.location_id, location.location_name, role.value, null)}
                        onEdit={() => openAssignDialog(location.location_id, location.location_name, role.value, mapping)}
                        onRemove={() => handleRemove(mapping?.id, role.value)}
                        saving={saving}
                      />
                    );
                  })}
                  
                  {/* Location-based Roles */}
                  {location.location_id !== null && locationBasedRoles.map(role => {
                    // Handle multi-assignable roles (like production_supervisor)
                    if (role.is_multi_assignable) {
                      const supervisors = location[`${role.value}s`] || location.production_supervisors || [];
                      const colors = getColorClasses(role.color);
                      const IconComponent = getIconComponent(role.icon);
                      
                      return (
                        <div 
                          key={role.value} 
                          className={`p-4 rounded-lg border ${supervisors.length ? `${colors.bg} ${colors.border}` : 'bg-gray-50 border-gray-200'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <IconComponent className={`w-4 h-4 ${colors.icon}`} />
                              <span className="font-medium text-sm text-gray-700">{role.label}s</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => openAssignDialog(location.location_id, location.location_name, role.value, null)}
                              title={`Add ${role.label}`}
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {supervisors.length > 0 ? (
                            <div className="space-y-2">
                              {supervisors.map(sup => (
                                <div key={sup.id} className={`flex items-center justify-between bg-white p-2 rounded border ${colors.border}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                      <span className="text-sm font-medium truncate">{sup.user_name}</span>
                                    </div>
                                    {(sup.process_names?.length > 0 || sup.process_name) && (
                                      <div className="flex flex-wrap gap-1 mt-1 ml-5">
                                        {(sup.process_names || [sup.process_name]).filter(Boolean).map((name, idx) => (
                                          <Badge key={idx} variant="outline" className={`text-xs ${colors.bg} px-1.5 py-0`}>
                                            {name}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0 ml-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={() => openAssignDialog(location.location_id, location.location_name, role.value, sup)}
                                      title="Edit"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleRemove(sup.id, role.value)}
                                      disabled={saving === sup.id}
                                      title="Remove"
                                    >
                                      {saving === sup.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <span>Not assigned</span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Single-assignable roles
                    const mapping = location[role.value] || location.all_mappings?.find(m => m.role === role.value);
                    return (
                      <RoleCard
                        key={role.value}
                        role={role.value}
                        label={role.label}
                        icon={getRoleIcon(role.value)}
                        mapping={mapping}
                        colorClass={getRoleColorClass(role.value)}
                        onAssign={() => openAssignDialog(location.location_id, location.location_name, role.value, null)}
                        onEdit={() => openAssignDialog(location.location_id, location.location_name, role.value, mapping)}
                        onRemove={() => handleRemove(mapping?.id, role.value)}
                        saving={saving}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Assignment Dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col" data-testid="assign-dialog">
            <div className="px-6 py-4 border-b flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold">
                  {isEditMode ? 'Edit' : 'Assign'} {getRoleLabel(assignTarget?.role)}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{assignTarget?.locationName}</p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowAssignDialog(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select User *</label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger data-testid="user-select">
                    <SelectValue placeholder="Choose a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show process selection for roles with sub_division */}
              {getRoleByKey(assignTarget?.role)?.has_sub_division && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned Processes
                    <span className="text-gray-400 font-normal ml-1">- select all applicable</span>
                  </label>
                  <div className="border rounded-lg p-3 max-h-60 overflow-y-auto bg-gray-50">
                    {processes.length === 0 ? (
                      <p className="text-sm text-gray-500">No processes found</p>
                    ) : (
                      <div className="space-y-2">
                        {processes.map(proc => (
                          <label 
                            key={proc.id} 
                            className="flex items-center gap-3 p-2 rounded hover:bg-white cursor-pointer transition-colors"
                          >
                            <Checkbox
                              checked={selectedProcessIds.includes(proc.id)}
                              onCheckedChange={() => toggleProcessSelection(proc.id)}
                            />
                            <span className="text-sm">{proc.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedProcessIds.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      {selectedProcessIds.length} process(es) selected
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleAssign} disabled={saving || !selectedUserId} data-testid="confirm-assign-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isEditMode ? 'Update' : 'Assign'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Roles Dialog */}
      {showManageRolesDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col" data-testid="manage-roles-dialog">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Manage Team Roles</h3>
                <p className="text-sm text-gray-500">Create, edit, or delete team roles</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => openRoleForm(null)}
                  data-testid="add-role-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Role
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowManageRolesDialog(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="space-y-3">
                {roles.map(role => {
                  const colors = getColorClasses(role.color);
                  const IconComponent = getIconComponent(role.icon);
                  return (
                    <div 
                      key={role.value || role.id} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${colors.bg} ${colors.border}`}
                      data-testid={`role-item-${role.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full bg-white border ${colors.border}`}>
                          <IconComponent className={`w-5 h-5 ${colors.icon}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{role.label}</span>
                            {role.is_system_role && (
                              <Badge variant="secondary" className="text-xs">System</Badge>
                            )}
                            {role.is_centralized && (
                              <Badge variant="outline" className="text-xs">Centralized</Badge>
                            )}
                            {role.is_multi_assignable && (
                              <Badge variant="outline" className="text-xs">Multi</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{role.description || `Key: ${role.value}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={() => openRoleForm(role)}
                          title="Edit role"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!role.is_system_role && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                            onClick={() => handleDeleteRole(role)}
                            disabled={savingRole}
                            title="Delete role"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {roles.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No roles defined yet. Click "Add Role" to create one.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Form Dialog (Create/Edit) */}
      {showRoleFormDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col" data-testid="role-form-dialog">
            <div className="px-6 py-4 border-b flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold">
                  {editingRole ? 'Edit Role' : 'Create New Role'}
                </h3>
                <p className="text-sm text-gray-500">
                  {editingRole?.is_system_role ? 'Some fields are restricted for system roles' : 'Define a new team role'}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowRoleFormDialog(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Role Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Key *</label>
                <Input
                  value={roleForm.role_key}
                  onChange={(e) => setRoleForm({...roleForm, role_key: e.target.value})}
                  placeholder="e.g., purchase_assistant"
                  disabled={editingRole?.is_system_role}
                  data-testid="role-key-input"
                />
                <p className="text-xs text-gray-400 mt-1">Unique identifier (lowercase, underscores)</p>
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Label *</label>
                <Input
                  value={roleForm.label}
                  onChange={(e) => setRoleForm({...roleForm, label: e.target.value})}
                  placeholder="e.g., Purchase Assistant"
                  data-testid="role-label-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                  placeholder="Brief description of this role"
                />
              </div>

              {/* Icon & Color Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <Select value={roleForm.icon} onValueChange={(val) => setRoleForm({...roleForm, icon: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(ICON_MAP).map(iconKey => {
                        const Icon = ICON_MAP[iconKey];
                        return (
                          <SelectItem key={iconKey} value={iconKey}>
                            <span className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {iconKey}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <Select value={roleForm.color} onValueChange={(val) => setRoleForm({...roleForm, color: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(COLOR_MAP).map(colorKey => {
                        const colors = COLOR_MAP[colorKey];
                        return (
                          <SelectItem key={colorKey} value={colorKey}>
                            <span className="flex items-center gap-2">
                              <span className={`w-4 h-4 rounded-full ${colors.bg} ${colors.border} border`}></span>
                              {colorKey}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Role Options */}
              {!editingRole?.is_system_role && (
                <div className="space-y-3 pt-2">
                  <label className="block text-sm font-medium text-gray-700">Role Options</label>
                  
                  <label className="flex items-center gap-3 p-2 rounded border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={roleForm.is_location_based}
                      onCheckedChange={(checked) => setRoleForm({...roleForm, is_location_based: checked})}
                    />
                    <div>
                      <span className="text-sm font-medium">Location Based</span>
                      <p className="text-xs text-gray-500">Role is assigned per location/unit</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-2 rounded border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={roleForm.is_centralized}
                      onCheckedChange={(checked) => setRoleForm({...roleForm, is_centralized: checked, is_location_based: checked ? false : roleForm.is_location_based})}
                    />
                    <div>
                      <span className="text-sm font-medium">Centralized</span>
                      <p className="text-xs text-gray-500">Role applies to all locations (shown in "All Locations" section)</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-2 rounded border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={roleForm.is_multi_assignable}
                      onCheckedChange={(checked) => setRoleForm({...roleForm, is_multi_assignable: checked})}
                    />
                    <div>
                      <span className="text-sm font-medium">Multi-Assignable</span>
                      <p className="text-xs text-gray-500">Multiple users can be assigned to this role per location</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-2 rounded border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={roleForm.has_sub_division}
                      onCheckedChange={(checked) => setRoleForm({...roleForm, has_sub_division: checked, sub_division_type: checked ? 'process' : null})}
                    />
                    <div>
                      <span className="text-sm font-medium">Has Sub-Division</span>
                      <p className="text-xs text-gray-500">Role can be further divided (e.g., by process)</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRoleFormDialog(false)} disabled={savingRole}>
                Cancel
              </Button>
              <Button onClick={handleSaveRole} disabled={savingRole || !roleForm.role_key.trim() || !roleForm.label.trim()} data-testid="save-role-btn">
                {savingRole ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

// Sub-component for Store Incharge and Purchase Manager cards
const RoleCard = ({ role, label, icon, mapping, colorClass, onAssign, onEdit, onRemove, saving }) => {
  const hasAssignment = !!mapping;
  
  return (
    <div className={`p-4 rounded-lg border ${hasAssignment ? colorClass.replace('text-', 'bg-').split(' ')[0] + ' border-' + colorClass.split('border-')[1] : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm text-gray-700">{label}</span>
        </div>
        {hasAssignment ? (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
              onClick={onEdit}
              title="Edit"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={onRemove}
              disabled={saving === mapping?.id}
              title="Remove"
            >
              {saving === mapping?.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={onAssign}
            title="Assign"
          >
            <UserPlus className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {hasAssignment ? (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium">{mapping.user_name}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Not assigned</span>
        </div>
      )}
    </div>
  );
};

export default LocationTeamMappingPage;
